import React from 'react';
import { NavLink } from 'react-router-dom';

export default function Sidebar({ isOpen, setIsOpen, isCollapsed, setIsCollapsed }) {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isTeacher = user?.role === "institute";
  const isSuperAdmin = user?.role === "superadmin";

  const handleLogout = () => {
    localStorage.removeItem("user");
    window.location.href = "/";
  };

  const navItems = [
    { label: "Dashboard", path: "/dashboard", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg> },
    isSuperAdmin && { label: "Superadmin Portal", path: "/superadmin", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"></path><path d="M2 17l10 5 10-5"></path><path d="M2 12l10 5 10-5"></path></svg> },
    !isTeacher && !isSuperAdmin && { label: "Student Profile", path: "/student-info", icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg> },
  ].filter(Boolean);

  const resourceItems = [
    { label: "Watch Demo", path: process.env.PUBLIC_URL + '/aboutdevice.mp4', external: true, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg> },
    { label: "Activation Video", path: "/activation-steps", external: false, icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect><line x1="7" y1="2" x2="7" y2="22"></line><line x1="17" y1="2" x2="17" y2="22"></line><line x1="2" y1="12" x2="22" y2="12"></line><line x1="2" y1="7" x2="7" y2="7"></line><line x1="2" y1="17" x2="7" y2="17"></line><line x1="17" y1="17" x2="22" y2="17"></line><line x1="17" y1="7" x2="22" y2="7"></line></svg> },
  ];

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-40 sm:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-screen bg-slate-950/80 backdrop-blur-xl border-r border-slate-800/60 flex flex-col transition-all duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'} ${isCollapsed ? 'w-20' : 'w-64'}`}>
        <div className={`flex h-16 items-center border-b border-slate-800/50 justify-between ${isCollapsed ? 'px-0 justify-center' : 'px-6'}`}>
          <div className="flex items-center gap-3 overflow-hidden">
             <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-sky-400 text-slate-950 font-bold shadow-lg shadow-indigo-500/20">
               P
             </div>
             {!isCollapsed && <span className="font-semibold text-sm tracking-[0.2em] text-slate-100 uppercase mt-0.5 whitespace-nowrap overflow-hidden">Pensense</span>}
          </div>
          <button onClick={() => setIsOpen(false)} className="sm:hidden text-slate-400 hover:text-white shrink-0 ml-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <div className="mx-4 mt-6 mb-2">
          {!isCollapsed ? (
            <p className="px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Navigation
            </p>
          ) : (
            <div className="h-4" />
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 flex flex-col gap-1.5 scrollbar-thin scrollbar-thumb-slate-800">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) => 
                `flex items-center gap-3 rounded-xl py-2.5 text-sm transition-all duration-200 ${isCollapsed ? 'justify-center px-0' : 'px-3'} ${
                  isActive 
                    ? "bg-sky-500/10 text-sky-400 font-medium border border-sky-500/20 shadow-sm shadow-sky-500/5 group" 
                    : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200 border border-transparent group"
                }`
              }
              title={isCollapsed ? item.label : undefined}
            >
              <div className={/* eslint-disable-next-line react/prop-types */ ({ isActive }) => isActive ? "text-sky-400" : "text-slate-500 group-hover:text-slate-300 transition-colors"}>
                {item.icon}
              </div>
              {!isCollapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}

          <div className="mt-4 mb-1">
            {!isCollapsed ? (
              <p className="px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Resources
              </p>
            ) : (
              <div className="h-4 border-t border-slate-800/50 mx-2" />
            )}
          </div>

          {resourceItems.map((item) => {
            if (item.external) {
              return (
                <a
                  key={item.label}
                  href={item.path}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 rounded-xl py-2.5 text-sm transition-all duration-200 text-slate-400 hover:bg-slate-800/60 hover:text-slate-200 border border-transparent group ${isCollapsed ? 'justify-center px-0' : 'px-3'}`}
                  title={isCollapsed ? item.label : undefined}
                >
                  <div className="text-slate-500 group-hover:text-slate-300 transition-colors">
                    {item.icon}
                  </div>
                  {!isCollapsed && <span className="truncate">{item.label}</span>}
                </a>
              );
            }
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) => 
                  `flex items-center gap-3 rounded-xl py-2.5 text-sm transition-all duration-200 ${isCollapsed ? 'justify-center px-0' : 'px-3'} ${
                    isActive 
                      ? "bg-sky-500/10 text-sky-400 font-medium border border-sky-500/20 shadow-sm shadow-sky-500/5 group" 
                      : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200 border border-transparent group"
                  }`
                }
                title={isCollapsed ? item.label : undefined}
              >
                <div className={/* eslint-disable-next-line react/prop-types */ ({ isActive }) => isActive ? "text-sky-400" : "text-slate-500 group-hover:text-slate-300 transition-colors"}>
                  {item.icon}
                </div>
                {!isCollapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </div>

        <div className="p-4 border-t border-slate-800/50 mt-auto flex flex-col gap-2">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`hidden sm:flex items-center gap-3 rounded-xl py-2.5 text-sm font-medium text-slate-400 transition-all hover:bg-slate-800/60 hover:text-slate-200 border border-transparent ${isCollapsed ? 'justify-center px-0' : 'px-3'}`}
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            <div className="text-slate-500 transition-transform duration-300" style={{ transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
            </div>
            {!isCollapsed && <span>Collapse</span>}
          </button>

          <button
            onClick={handleLogout}
            className={`flex w-full items-center gap-3 rounded-xl py-2.5 text-sm font-medium text-slate-400 transition-all hover:bg-rose-500/10 hover:text-rose-400 border border-transparent hover:border-rose-500/20 ${isCollapsed ? 'justify-center px-0' : 'px-3'}`}
            title={isCollapsed ? "Logout" : undefined}
          >
            <div className="text-slate-500 hover:text-rose-400 transition-colors">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
            </div>
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
